import React, { useContext, useEffect, useState } from 'react'
import useGetModelDetails from './hooks/useGetModelDetails'
import { DocumentTitle } from '../../components/DocumentTitle/DocumentTitle'
import { Button, Card, Divider, Loading, Modal, Option, Select, SingleDatePicker, Tag, Text, Tooltip } from '@sede-enterprise/shell-ds-react-framework'
import { splitToArray } from '../../util/helperFunctions'
import moment from 'moment'
import { handleDateChange } from '../../util/handleDateChange'
import momentGenerateConfig from 'rc-picker/lib/generate/moment'
import CardContainer from './components/CardContainer'
import { InfoCircle } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components'
import { helperTexts } from '../../util/helperTexts'
import { modelStatusSentimentMap } from '../../util/constants'
import TopContributors from './components/TopContributors'
import { useHistory } from 'react-router-dom/cjs/react-router-dom'
import useModelTrainingJobStatus from './hooks/useModelTrainingJobStatus'
import { toast } from 'react-toastify'
import SesnorPromoteStatus from './components/SesnorPromoteStatus'
import { UserContext } from '../../App'
import useLogReport from '../../util/hooks/useLogReport'
import { azureFetch } from '../../api/azureFetch'


const DEFAULT_STATS_SPEC = {
    initStart: moment().subtract(2, "year"),
    initEnd: moment(),
};

const modelHelp = helperTexts.modelTraining;

const ModelDetails = ({ match }) => {

    // Log report for the page
    useLogReport('CAM DETAILS');

    const { modelDetails, loading } = useGetModelDetails(match.params.id);
    const [selectedSensors, setSelectedSensors] = useState([]);


    const [startDate, setStartDate] = useState(DEFAULT_STATS_SPEC.initStart);
    const [endDate, setEndDate] = useState(DEFAULT_STATS_SPEC.initEnd);


    const [startErrorText, setStartErrorText] = useState(null);
    const [inValidStartDate, setInValidStartDate] = useState(null);

    const [endErrorText, setEndErrorText] = useState(null);
    const [inValidEndDate, setInValidEndDate] = useState(null);

    const { userData } = useContext(UserContext);

    const [promoteJobId, setPromoteJobId] = useState(null);

    const [open, setOpen] = useState(false);
    const handleOnClose = () => setOpen(false);


    useEffect(() => {
        if (modelDetails?.sensors?.length > 0) {
            // Parse sensors to extract id and other properties
            const parsedSensors = modelDetails.sensors.map(sensor => JSON.stringify(sensor));
            // Set the first three sensors as selected
            setSelectedSensors(parsedSensors.slice(0, 3));
        }
    }, [modelDetails]);

    const history = useHistory()

    // Fetch model training job status
    // This will redirect to the job status page if the model is still being trained
    // and will show loading or error states accordingly
    const { modelStatusLoading, modelStatusError } = useModelTrainingJobStatus(match.params.id, modelDetails);

    useEffect(() => {
        if (modelDetails?.id) {
            // Fetch the PromoteModelToLiveJob to check if there is an ongoing job for this model
            azureFetch(`getRunningPromoteToLiveJobId/${modelDetails.id}`).then((res) => {
                let job_id = res?.job_id
                if (job_id) {
                    setPromoteJobId(job_id);
                }
            }).catch((error) => {
                console.error("Error fetching PromoteModelToLiveJob:", error);
                toast.error("Error fetching PromoteModelToLiveJob");
            });
        }
    }, [modelDetails]);

    const promoteToChampion = () => {
        const hasChampion = selectedSensors.some(sensorStr => {
            const sensor = JSON.parse(sensorStr);
            return sensor.status === "LIVE";
        });

        if (hasChampion) {
            toast.error("Choose only Observation sensors to promote to champion");
            return;
        }

        const sensors = selectedSensors.map(sensorStr => {
            const sensor = JSON.parse(sensorStr);
            return sensor.id;
        });

        const sites = userData?.map((user => user.target.id)) || [];

        azureFetch("triggerPromoteToLiveJob", {
            sensors: sensors,
            "model_id": modelDetails.id
        }).then((res) => {
                toast.success("Promote to Live job started successfully");
                setPromoteJobId(res.job_id);
            }).catch((error) => {
                console.log(error);
                toast.error("Error starting Promote to Live job");
            })
    }



    return (
        <>
            <DocumentTitle title='CARMA - Model Details' />
            <div className='parent-container'>
                <div className='content-container'>
                    {modelStatusLoading && <div className='loading-container model-page-load'><Loading /></div>}
                    {modelStatusError && <div className='error-container'>
                        <p className='error-message'>{modelStatusError}</p>
                    </div>}
                    {!modelStatusLoading && !modelStatusError && modelDetails && <>
                        <div className='content-header'>
                            <h2>Model Details</h2>
                            <Button type='primary' onClick={() => history.push(`/models/create/${modelDetails?.id}`)} disabled={!modelDetails}>Train New Model</Button>
                        </div>
                        <div className='content-body'>
                            <Card className='details-card' bodyPadding={false}>
                                {loading && <div className='loading-container'><Loading /></div>}
                                {modelDetails && <div className='details-card-content'>
                                    <div className='details-card-content-col'>
                                        <div className='info-container'>
                                            <div className='info-data'>
                                                <p className='data-label'>Model ID</p>
                                                <p className='data-value'>{modelDetails.id}</p>
                                            </div>
                                            <div className='info-data'>
                                                <p className='data-label'>Model Name</p>
                                                <p className='data-value'>{modelDetails.name}</p>
                                            </div>
                                            <div className='info-data'>
                                                <p className='data-label'>Model Type</p>
                                                <p className='data-value'>{modelDetails.model_type}</p>
                                            </div>

                                            <div className='info-data'>
                                                <p className='data-label'>Model Status</p>
                                                {
                                                    modelDetails?.status && modelDetails?.status.map((status) => (

                                                        <div className='model-status-tag-container' key={status.name}>
                                                            <Tag
                                                                key={status.name}
                                                                sentiment={modelStatusSentimentMap[status.name]}
                                                                variant='outlined'
                                                                size='small'
                                                                shape='round'
                                                            >
                                                                <span className='status-count'>{status.count}</span>
                                                                {status.name}
                                                            </Tag>
                                                        </div>
                                                    )
                                                    )
                                                }

                                            </div>

                                        </div>
                                    </div>

                                    <div className='details-card-content-col'>
                                        <Divider direction='vertical' className='vertical-divider' />
                                        <div className='info-container'>
                                            <div className='info-data'>
                                                <p className='data-label'>Features</p>
                                                <p className='data-value'>{modelDetails.features.join(', ')}</p>
                                            </div>
                                            <div className='info-data'>
                                                <p className='data-label'>Mask</p>
                                                <p className='data-value'>{modelDetails.mask}</p>
                                            </div>
                                            <div className='info-data'>
                                                <p className='data-label'>Target</p>
                                                <p className='data-value'>{modelDetails.target}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='details-card-content-col'>
                                        <Divider direction='vertical' className='vertical-divider' />
                                        <div className='info-container'>
                                            <div className='info-data'>
                                                {modelDetails.train_start_ts && <> <p className='data-label'>Train Start</p>
                                                    <p className='data-value'>{splitToArray(modelDetails.train_start_ts)[0]}</p></>}

                                            </div>
                                            <div className='info-data'>
                                                {modelDetails.train_end_ts && <>  <p className='data-label'>Train End</p>
                                                    <p className='data-value'>{splitToArray(modelDetails.train_end_ts)[0]}</p>
                                                </>
                                                }

                                            </div>
                                        </div>
                                    </div>
                                </div>}



                            </Card>
                            <br />

                            {promoteJobId && <SesnorPromoteStatus id={promoteJobId} setSelectedSensors={setSelectedSensors} />}

                            <br />
                            <h4>Model Performance</h4>
                            <div className='model-results-actions-parent'>
                                <div className='model-results-actions'>
                                    <div className='sensor-list-selection'>
                                        <div className='label-header'>
                                            <p className='data-label'>Select Sensors/CMLs</p>
                                            <Tooltip className='tooltip' placement='bottom' overlay={<div className='tooltip-content'>{modelHelp.select}</div>} trigger={
                                                <Button variant='transparent' iconOnly icon={<InfoCircle />} />
                                            } />
                                        </div>

                                        <Select value={selectedSensors} mode='multiple' onChange={value => {
                                            setSelectedSensors(value)
                                        }
                                        } allowClear={false} className='sensor-select'>
                                            {modelDetails?.sensors?.map(opt => {

                                                return <Option key={JSON.stringify(opt)}>
                                                    {opt.id}
                                                </Option>
                                            })}
                                        </Select>
                                    </div>
                                    <div className='chart-date-container'>
                                        <div className='start-date-wrapper'>
                                            <div className='label-header'>
                                                <p className="data-label">Start Date</p>
                                                <Button variant='transparent' iconOnly />
                                            </div>
                                            <SingleDatePicker
                                                generateConfig={momentGenerateConfig}
                                                config="custom"
                                                format="DD/MM/yyyy"
                                                disabledDate={(curr) => curr ? curr.isAfter(moment().toDate()) : false}
                                                value={!inValidStartDate && startDate ? startDate : inValidStartDate ? inValidStartDate : null}
                                                onChange={(date) => {
                                                    setStartErrorText(null)
                                                    setInValidStartDate(null)
                                                    if (date.isBefore(endDate)) {
                                                        handleDateChange(date, startDate, setStartDate, "DD/MM/yyyy")
                                                    }
                                                    else if (date.isAfter(endDate)) {
                                                        setStartErrorText("Start date cannot be after end date")
                                                        setInValidStartDate(date)
                                                    }
                                                }
                                                }
                                                allowClear={false} />
                                            <p className="data-label error-label">{startErrorText}</p>
                                        </div>
                                        <div className='end-date-wrapper'>
                                            <div className='label-header'>
                                                <p className="data-label">End Date</p>
                                                <Button variant='transparent' iconOnly />
                                            </div>
                                            <SingleDatePicker
                                                generateConfig={momentGenerateConfig}
                                                config="custom"
                                                format="DD/MM/yyyy"
                                                disabledDate={(curr) => curr ? curr.isAfter(moment().toDate()) : false}
                                                value={!inValidEndDate && endDate ? endDate : inValidEndDate ? inValidEndDate : null}
                                                onChange={(date) => {
                                                    setEndErrorText(null)
                                                    setInValidEndDate(null)
                                                    if (date.isAfter(startDate)) {
                                                        handleDateChange(date, endDate, setEndDate, "DD/MM/yyyy")
                                                    }
                                                    else if (date.isBefore(startDate)) {
                                                        setEndErrorText("End date cannot be before start date")
                                                        setInValidEndDate(date)
                                                    }
                                                }
                                                }
                                                allowClear={false} />
                                            <p className="data-label error-label">{endErrorText}</p>
                                        </div>
                                    </div>
                                </div>
                                <Button className='model-results-action-button' onClick={() => setOpen(true)} disabled={selectedSensors?.length <= 0 || promoteJobId}>Promote to Live</Button>
                            </div>
                            <br />
                            <div className='model-results-chart-parent'>
                                {selectedSensors?.length > 0 && selectedSensors?.map((sensor) => {
                                    sensor = JSON.parse(sensor);
                                    return <CardContainer endDate={endDate} startDate={startDate}
                                        key={sensor} sensor={sensor} selectedSensors={selectedSensors}
                                        modelDetails={modelDetails} />
                                }
                                )}
                            </div>

                            <br />
                            {modelDetails && modelDetails?.status.some(obj => {
                                return obj.name === "OBSERVATION"
                            }) && <TopContributors modelDetails={modelDetails} />}
                        </div>
                    </>}



                </div>

                <br />
            </div>

            <Modal open={open} onClose={handleOnClose} size='large'

                title='Confirm Promote to Live'
                actions={[{
                    label: 'Cancel',
                    action: () => {
                        handleOnClose();
                    },
                    props: {
                        variant: 'outlined',
                    }

                }, {
                    label: 'Confirm',
                    action: () => {
                        handleOnClose();
                        promoteToChampion()
                    }
                }]}>

                <Text>
                    You are about to promote sensors : <b>{selectedSensors?.map((sensor) => JSON.parse(sensor).id).join('; ')} </b> as LIVE with this model. This would move the existing model to OBSERVATION and alerts related to the sensors will be re-created.
                </Text>
            </Modal>

        </>
    )
}

export default ModelDetails